import { GoogleBasemapAdapter } from "./google-adapter";
import type { WebBasemapAdapter } from "./web-basemap";

/**
 * 어느 바닥 지도를 쓸지는 **빌드가 정한다.**
 *
 * - `VITE_GOOGLE_MAPS_API_KEY` 가 있으면 Google (운영·미리보기).
 * - 없고 개발 빌드(`import.meta.env.DEV`)면 Mock — 격자 위에 핀만. 개발·시험용이다.
 * - 없고 운영 빌드(PROD)면 **던진다.** 운영에 격자 지도를 조용히 내보내지 않는다 (2026-09-11 의 실수).
 *
 * `import.meta.env.PROD` 는 빌드 상수라 운영 번들에서는 Mock 갈래가 통째로 사라진다 — `scripts/assert-basemap.mjs`
 * 가 번들에 Mock 표식이 없는지, SDK 호스트가 있는지 확인한다.
 */
export interface BasemapEnv { PROD: boolean; DEV: boolean; VITE_GOOGLE_MAPS_API_KEY?: string; }

export function basemapChoice(env: BasemapEnv): { kind: "google"; key: string } | { kind: "mock" } {
  const key = (env.VITE_GOOGLE_MAPS_API_KEY ?? "").trim();
  if (key) return { kind: "google", key };
  if (env.PROD) throw new Error("운영 빌드에 바닥 지도 키가 없습니다 (VITE_GOOGLE_MAPS_API_KEY). Mock 으로 대신하지 않습니다.");
  return { kind: "mock" };
}

export async function createBasemapAdapter(env: BasemapEnv = import.meta.env as unknown as BasemapEnv): Promise<WebBasemapAdapter> {
  const choice = basemapChoice(env);
  if (choice.kind === "google") return new GoogleBasemapAdapter(choice.key);
  /*
   * Mock 은 **개발 빌드에서만 번들에 들어간다.** `import.meta.env.DEV` 는 빌드 상수라 운영 빌드에서는
   * 이 삼항의 앞 갈래(동적 import)가 코드째 사라진다 — assert-basemap.mjs 가 그것을 확인한다.
   */
  const mod = import.meta.env.DEV ? await import("./mock-adapter") : null;
  if (!mod) throw new Error("운영 빌드에는 Mock 바닥 지도가 없습니다.");
  return new mod.MockMapAdapter();
}
